import advancedFormat from "dayjs/plugin/advancedFormat";
import { useMemo, useRef } from "react";
import dayjs from "dayjs";

import useQueriedRecords from "../../../api/api-hooks/records/useQueriedRecords";
import { Intervals } from "../../../api/api-hooks/records/useQueriedRecords";
import { timeFrameFormatting } from "../../../utils/DashboardUtils";
import { currencyToReadable } from "../../../utils/DashboardUtils";
import { GetGroupByFieldAndType } from "../../../utils/ChartUtils";
import useFieldMap from "../../../hooks/useFieldMap";
import useAllTags from "../../../hooks/useAllTags";
import FieldUtils from "../../../utils/FieldUtils";
import useModule from "../../../hooks/useModule";

dayjs.extend(advancedFormat);

interface Props {
  moduleAPIName: string;
  query: any;
  topLevelFilters: any[] | undefined;
  viewFilters: any[] | undefined;
  validator: (query: any) => boolean;
  record: any;
  queryKeyID: string;
}

const useGroupedChartData = ({
  moduleAPIName,
  query,
  topLevelFilters = undefined,
  viewFilters = undefined,
  validator,
  record,
  queryKeyID,
}: Props) => {
  const fieldTypeMap = useFieldMap(moduleAPIName, "FieldType");
  const fieldNameMap = useFieldMap(moduleAPIName, "Name");
  const fieldMap = useFieldMap(moduleAPIName);
  const { getTagNames } = useAllTags(moduleAPIName);
  const { getModule } = useModule();

  const variableValues = useMemo(() => {
    let vals: Record<string, any> = {};
    if (record) {
      vals["record"] = record;
    }

    return vals;
  }, [record]);

  const { records, recordsLoading } = useQueriedRecords({
    variableValues: variableValues,
    moduleAPIName: moduleAPIName,
    query: query,
    topLevelFilters: topLevelFilters,
    viewFilters: viewFilters,
    queryValidator: validator,
    useDefaultVariables: true,
    queryKeyID: queryKeyID,
  });
  const recordsRef = useRef<Record<string, any>[]>();
  recordsRef.current = records;

  const { groupByField, groupByFieldType, groupByFieldName } = useMemo(() => {
    return GetGroupByFieldAndType({
      groupByField: query?.GroupBy[0]?.Field,
      fieldMap,
      getModule,
    });
  }, [query?.GroupBy[0]]);

  const {
    groupByField: xAxisField,
    groupByFieldType: xAxisFieldType,
    groupByFieldName: xAxisFieldName,
  } = useMemo(() => {
    return GetGroupByFieldAndType({
      groupByField: query?.GroupBy[1]?.Field,
      fieldMap,
      getModule,
    });
  }, [query?.GroupBy[1]]);

  const aggregationField = query?.Aggregations?.[0]?.Field;
  const aggregationMethod = query?.Aggregations?.[0]?.Method;
  const stringifiedRecords = JSON.stringify(records);

  const data = useMemo(() => {
    const records = recordsRef.current;

    if (!records || !records?.length) return { data: null, keys: null };

    const dataObject: Record<string, any> = {};
    const keys = new Set<string>();
    const references = new Map();
    const aggregationFieldType = fieldTypeMap[aggregationField];

    records?.forEach((record) => {
      let xAxisValue = record.GroupBy?.[xAxisField];
      const groupByValue = record.GroupBy?.[groupByField];
      let aggregationValue = record[aggregationMethod][aggregationField];
      let readable = record[aggregationMethod][aggregationField];
      const interval: Intervals = query?.GroupBy?.[1]?.Interval;
      const isDateTime =
        xAxisFieldType === "Date" || xAxisFieldType === "DateTime";
      const isGroupByReferenceField = FieldUtils.isReference({
        FieldType: groupByFieldType,
      });
      const groupByFieldValue = isGroupByReferenceField
        ? groupByValue?._RecordName ?? groupByValue
        : groupByValue;

      if (FieldUtils.isReference({ FieldType: xAxisFieldType })) {
        if (xAxisValue === null) {
          xAxisValue = `No ${fieldNameMap?.[xAxisField]}`;
        } else {
          xAxisValue = xAxisValue?._RecordName ?? "(No Name)";
        }
      }

      if (
        aggregationFieldType === "CurrencyInterval" ||
        aggregationFieldType === "Currency"
      ) {
        readable = currencyToReadable(aggregationValue);

        if (readable === "(unknown currency)") {
          readable = "--";
          aggregationValue = 0;
        } else {
          aggregationValue = aggregationValue.Value;
        }
      }

      if (aggregationFieldType === "Percent") {
        readable = aggregationValue + "%";
        aggregationValue = Number(aggregationValue);
      }

      // Skip over null xAxisValue and groupByValue cases.
      if (!xAxisValue || !groupByValue) return;

      if (groupByFieldType === "MultiSelect") {
        // The data is provided as an array of strings (only contains one element in it)
        let key = typeof Array.isArray(groupByValue)
          ? groupByValue[0]
          : groupByValue;
        keys.add(key);
      } else {
        keys.add(groupByFieldValue);
      }

      if (isGroupByReferenceField) {
        references.set(groupByFieldValue, groupByValue?.id);
      }

      if (
        typeof aggregationValue === "object" &&
        Object.keys(aggregationValue).length < 1
      ) {
        aggregationValue = 0;
        readable = "--";
      }

      if (isDateTime) {
        // @ts-ignore
        xAxisValue = dayjs(xAxisValue).format(timeFrameFormatting[interval]);
      }

      if (xAxisFieldType === "Tags") {
        xAxisValue = getTagNames(xAxisValue) || "No Tags";
      }

      if (!dataObject[xAxisValue]) {
        dataObject[xAxisValue] = {
          [groupByFieldValue]: {
            ...(isGroupByReferenceField && { OriginalValue: groupByValue }),
            val: aggregationValue,
            readable: readable,
            ...(groupByFieldType === "Tags" && {
              Name: record.GroupBy?.Name,
            }),
          },
        };
      } else {
        dataObject[xAxisValue][groupByFieldValue] = {
          ...(isGroupByReferenceField && { OriginalValue: groupByValue }),
          val: aggregationValue,
          readable: readable,
          ...(groupByFieldType === "Tags" && {
            Name: record.GroupBy?.Name,
          }),
        };
      }
    });

    const result = Object.keys(dataObject).map((key) => {
      return {
        [xAxisField]: key,
        ...dataObject[key],
      };
    });

    return { data: result, keys, references };
  }, [
    stringifiedRecords,
    aggregationField,
    aggregationMethod,
    groupByField,
    xAxisField,
    fieldTypeMap,
    query?.GroupBy,
    groupByField,
    xAxisField,
  ]);

  return {
    data: data.data,
    keys: data.keys,
    references: data.references,
    error:
      !validator(query) || (!recordsLoading && (!records || !records.length)),
    loading: recordsLoading || !data.data,
    aggregationField,
    aggregationMethod,
    xAxisField,
    xAxisFieldName,
    xAxisFieldType,
    groupByField,
    groupByFieldType,
    groupByFieldName,
  };
};

export default useGroupedChartData;
